import { formatTime } from '@/utils/datetime'

export const MIN_CAPTURE_DAYS = 14
export const DAY_MS = 24 * 60 * 60 * 1000
const DATE_FORMAT = 'D MMM YYYY'

export const formatDate = (dateStr: string | null | undefined, tz?: string | null): string => {
  if (!dateStr) return '--'
  return formatTime(dateStr, tz || 'UTC', DATE_FORMAT)
}

export const formatNumber = (value: number | null | undefined, digits = 0): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '--'
  return value.toLocaleString(undefined, { maximumFractionDigits: digits })
}

export const normalizeUnits = (units?: string | null): string => {
  const u = (units ?? '').toLowerCase()
  if (u === 'sqkm' || u === 'km²' || u === 'km2') return 'km²'
  if (u === 'hectare' || u === 'ha') return 'ha'
  if (u === 'credits' || u === 'credit') return 'credits'
  return units ?? ''
}

export const formatQuota = (value: number | null | undefined, units?: string | null, digits = 2): string => {
  if (value === null || value === undefined) return '--'
  const u = normalizeUnits(units)
  return u ? `${formatNumber(value, digits)} ${u}` : formatNumber(value, digits)
}

export const daysBetween = (a: string | null | undefined, b: string | null | undefined): number | null => {
  if (!a || !b) return null
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / DAY_MS)
}

/** Whole days from now until the date; negative when already past. */
export const daysUntil = (dateStr: string | null | undefined): number | null => {
  if (!dateStr) return null
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / DAY_MS)
}

export const LAYER_TYPE_LABELS: Record<string, string> = {
  order: 'Order',
  subscription: 'Subscription',
  tasking: 'Tasking',
}

export type StatusGroup = 'in_progress' | 'done' | 'failed' | 'rejected'

export const statusGroup = (status: number): StatusGroup => {
  if (status === -100) return 'rejected'
  if (status === 0) return 'done'
  if (status < 0) return 'failed'
  return 'in_progress'
}

export const STATUS_GROUP_LABELS: Record<StatusGroup, string> = {
  in_progress: 'In progress',
  done: 'Done',
  failed: 'Failed',
  rejected: 'Rejected',
}

/** Primary date row for a request: capture window (tasking), capture date (order) or date range (subscription). */
export const requestDateRow = (layer: { layer_type: string; data: Record<string, any> | null }, tz?: string | null): { label: string; value: string } | null => {
  const data = layer.data ?? {}
  if (layer.layer_type === 'tasking' && (data.start_time || data.end_time)) {
    const days = daysBetween(data.start_time, data.end_time)
    return { label: 'Capture', value: `${formatDate(data.start_time, tz)} — ${formatDate(data.end_time, tz)}${days != null ? ` (${days} days)` : ''}` }
  }
  if (layer.layer_type === 'order' && data.capture_time) {
    return { label: 'Captured', value: formatDate(data.capture_time, tz) }
  }
  if (layer.layer_type === 'subscription' && (data.parameters?.start_date || data.parameters?.end_date)) {
    return { label: 'Range', value: `${formatDate(data.parameters?.start_date, tz)} — ${formatDate(data.parameters?.end_date, tz)}` }
  }
  return null
}
